import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { InstitutionExtended } from '../../../../api/app-types/institution.app';
import { Workplace } from '../../../../api/app-types/workplace.app';
import { USER_SERVICE, UserApi } from '../../../../api/services/user.service';
import { WORKPLACE_SERVICE } from '../../../../api/services/workplace.api';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { WorkplaceList } from '../workplace-list/workplace-list';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { NotificationService } from '../../../../shared/notification/notification.service';
import { createErrorNotification } from '../../../../shared/notification/notification.factory';
import { User } from '../../../../api/app-types/user.app';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-workplace-view',
	imports: [TranslocoModule, WorkplaceList, LvrHeading, LvrButton],
	templateUrl: './workplace-view.html',
	styleUrl: './workplace-view.scss',
	providers: [
		{
			provide: USER_SERVICE,
			useClass: UserApi,
		},
		{
			provide: NOTIFICATION_SERVICE,
			useClass: NotificationService,
		},
	],
})
export class WorkplaceView {
	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(WORKPLACE_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly session = inject(SessionManagerService);
	private readonly data = toSignal(this.route.data);
	private readonly hasInstitutionCreatePermission = hasPermission(this.session.permissions, 'Institutions.Create');
	public readonly canCreateWorkplace = computed(() => this.hasInstitutionCreatePermission());
	public readonly institution = computed(() => this.data()!['institution'] as InstitutionExtended);
	public readonly user = computed(() => this.data()!['user'] as User);
	public readonly workplaces = computed(() => this.page().entities);
	public readonly loading = signal(false);

	private readonly initialPage = computed<PaginatedResponse<Workplace>>(() => {
		const data = this.data();
		return data && 'workplace' in data
			? (data['workplace'] as PaginatedResponse<Workplace>)
			: { entities: [], offset: 0, range: 0, totalEntities: 0 };
	});

	public readonly page = linkedSignal({
		source: this.initialPage,
		computation: (init) => ({
			...init,
			range: init.range || this.env.pagination.range,
		}),
	});

	public async onPageRequested(event: { pageIndex: number; pageSize: number }) {
		this.loading.set(true);
		try {
			// Skip represents the items to skip, the offset. BE uses skip here, not page number.
			const pageIndex = event.pageIndex;
			const pageSize = event.pageSize;
			const skip = pageIndex * pageSize;

			const updated = await this.api.getAllByInstitutionId(this.institution().id, skip, pageSize);
			this.page.set({ ...updated, offset: pageIndex, range: pageSize });
		} catch {
			this.ntfy.addNotification(createErrorNotification('Fehler beim Neuladen der Arbeitsplätze'));
		} finally {
			this.loading.set(false);
		}
	}

	public openCreate() {
		if (!this.canCreateWorkplace()) return;
		this.router.navigate(['add'], { relativeTo: this.route });
	}
}
